<template>
    <ContainerContent
        class="mb-16"
        max-class="max-w-sm-ceil"
        outer-container
    >
        <div
            class="my-4"
        >
            <h1 class="page-title">Privacy Policy</h1>
            <i>Last updated October 29, 2019</i>
        </div>

        <p>
            Thank you for choosing to be part of our community at String King Lacrosse LLC, doing business as
            StringKing (&#8220;
            <strong>StringKing</strong>
            &#8221;, &#8220;
            <strong>we</strong>
            &#8221;, &#8220;
            <strong>us</strong>
            &#8221;, or &#8220;
            <strong>our</strong>
            &#8221;). We are committed to protecting your personal information and your right to privacy. If you
            have any questions or concerns about our policy, or our practices with regards to your personal
            information, please contact us at
            <a href="mailto:marketing@stringking.com">marketing@stringking.com</a>
            .
        </p>
        <p>
            When you visit our website stringking.com, and use our services, you trust us with your personal
            information. We take your privacy very seriously. In this privacy policy, we seek to explain to you
            in the clearest way possible what information we collect, how we use it and what rights you have in
            relation to it. We hope you take some time to read through it carefully, as it is important. If
            there are any terms in this privacy policy that you do not agree with, please discontinue use of our
            Sites and our services.
        </p>
        <p>
            This privacy policy applies to all information collected through our website (such as
            stringking.com), and/or any related services, sales, marketing or events (we refer to them
            collectively in this privacy policy as the &#8220;
            <strong>Services</strong>
            &#8221;).
        </p>
        <p>
            <strong>
                Please read this privacy policy carefully as it will help you make informed decisions about
                sharing your personal information with us.
            </strong>
        </p>

        <h3>Table of contents</h3>

        <ol class="my-4">
            <li><a href="#1-what-information-do-we-collect">What information do we collect?</a></li>
            <li><a href="#2-how-do-we-use-your-information">How do we use your information?</a></li>
            <li>
                <a href="#3-will-your-information-be-shared-with-anyone">
                    Will your information be shared with anyone?
                </a>
            </li>
            <li>
                <a href="#4-who-will-your-information-be-shared-with">
                    Who will your information be shared with?
                </a>
            </li>
            <li><a href="#5-how-long-do-we-keep-your-information">How long do we keep your information?</a></li>
            <li><a href="#6-how-do-we-keep-your-information-safe">How do we keep your information safe?</a></li>
            <li>
                <a href="#7-do-we-collect-information-from-minors">Do we collect information from minors?</a>
            </li>
            <li><a href="#8-what-are-your-privacy-rights">What are your privacy rights?</a></li>
            <li><a href="#9-data-breach">Data breach</a></li>
            <li><a href="#10-controls-for-do-not-track-features">Controls for do-Not-Track features</a></li>
            <li>
                <a href="#11-do-california-residents-have-specific-privacy-rights">
                    Do California residents have specific privacy rights?
                </a>
            </li>
            <li><a href="#12-do-we-make-updates-to-this-policy">Do we make updates to this policy?</a></li>
            <li>
                <a href="#13-how-can-you-contact-us-about-this-policy">
                    How can you contact us about this policy?
                </a>
            </li>
        </ol>

        <ol>
            <li id="1-what-information-do-we-collect">
                <h3>What information do we collect?</h3>

                <h4>Personal information you disclose to us</h4>

                <p>
                    <strong>In Short:</strong>
                    We collect personal information that you provide to us such as name, address, contact
                    information, passwords and security data, and payment information.
                </p>

                <p>
                    We collect personal information that you voluntarily provide to us when registering at the
                    Services expressing an interest in obtaining information about us or our products and
                    services, when participating in activities on the Services or otherwise contacting us.
                </p>
                <p>
                    The personal information that we collect depends on the context of your interactions with us
                    and the Services, the choices you make and the products and features you use. The personal
                    information we collect can include the following:
                </p>

                <ul class="mb-4">
                    <li>
                        <strong>Personal Information Provided by You.</strong>
                        We collect data collected from surveys, purchase history, financial information (credit
                        card number, purchase history, invoices), and other similar data.
                    </li>
                    <li>
                        <strong>Credentials.</strong>
                        We collect passwords, password hints, and similar security information used for
                        authentication and account access.
                    </li>
                    <li>
                        <strong>Payment Data.</strong>
                        We collect data necessary to process your payment if you make purchases, such as your
                        payment instrument number (such as a credit card number), and the security code
                        associated with your payment instrument. All payment data is stored by Stripe. You may
                        find their privacy policy link(s) here: stripe.com.
                    </li>
                </ul>
                <p>
                    All personal information that you provide to us must be true, complete and accurate, and you
                    must notify us of any changes to such personal information.
                </p>

                <h4>Information automatically collected</h4>
                <p>
                    <strong>In Short:</strong>
                    Some information – such as IP address and/or browser and device characteristics – is
                    collected automatically when you visit our Services.
                </p>
                <p>
                    We automatically collect certain information when you visit, use or navigate the Services.
                    This information does not reveal your specific identity (like your name or contact
                    information) but may include device and usage information, such as your IP address, browser
                    and device characteristics, operating system, language preferences, referring URLs, device
                    name, country, location, information about how and when you use our Services and other
                    technical information. This information is primarily needed to maintain the security and
                    operation of our Services, and for our internal analytics and reporting purposes.
                </p>
                <p>
                    <strong>Online Identifiers.</strong>
                    We collect cookie identifiers, or others such as the ones used for analytics and marketing,
                    device's geolocation, and other similar data.
                </p>
            </li>

            <li id="2-how-do-we-use-your-information">
                <h3>How do we use your information?</h3>

                <p>
                    <strong>In Short:</strong>
                    We process your information for purposes based on legitimate business interests, the
                    fulfillment of our contract with you, compliance with our legal obligations, and/or your
                    consent.
                </p>
                <p>
                    We use personal information collected via our Services for a variety of business purposes
                    described below. We process your personal information for these purposes in reliance on our
                    legitimate business interests, in order to enter into or perform a contract with you, with
                    your consent, and/or for compliance with our legal obligations. We indicate the specific
                    processing grounds we rely on next to each purpose listed below.
                </p>
                <p>We use the information we collect or receive:</p>
                <ul class="mb-4">
                    <li>
                        <strong>To facilitate account creation and login process.</strong>
                        If you choose to link your account with us to a third party account (such as your Google
                        or Facebook account), we use the information you allowed us to collect from those third
                        parties to facilitate account creation and logon process for the performance of the
                        contract.
                    </li>
                    <li>
                        <strong>To send you marketing and promotional communications.</strong>
                        We and/or our third party marketing partners may use the personal information you send
                        to us for our marketing purposes, if this is in accordance with your marketing
                        preferences. You can opt-out of our marketing emails at any time (see
                        <a href="#8-what-are-your-privacy-rights">WHAT ARE YOUR PRIVACY RIGHTS</a>
                        below).
                    </li>
                    <li>
                        <strong>To send administrative information to you.</strong>
                        We may use your personal information to send you product, service and new feature
                        information and/or information about changes to our terms, conditions, and policies.
                    </li>
                    <li>
                        <strong>To fulfill and manage your orders.</strong>
                        We may use your information to fulfill and manage your orders, payments, returns, and
                        exchanges made through the Services.
                    </li>
                    <li>
                        <strong>To deliver targeted advertising to you.</strong>
                        We may use your information to develop and display content and advertising (and work
                        with third parties who do so) tailored to your interests and/or location and to measure
                        its effectiveness.
                    </li>
                    <li>
                        <strong>
                            To enforce our terms, conditions and policies for Business Purposes, Legal Reasons
                            and Contractual.
                        </strong>
                    </li>
                    <li>
                        <strong>To respond to legal requests and prevent harm.</strong>
                        If we receive a subpoena or other legal request, we may need to inspect the data we hold
                        to determine how to respond.
                    </li>
                    <li>
                        <strong>To manage user accounts.</strong>
                        We may use your information for the purposes of managing our account and keeping it in
                        working order.
                    </li>
                    <li>
                        <strong>To deliver services to the user.</strong>
                        We may use your information to provide you with the requested service.
                    </li>
                    <li>
                        <strong>To respond to user inquiries/offer support to users.</strong>
                        We may use your information to respond to your inquiries and solve any potential issues
                        you might have with the use of our Services.
                    </li>
                    <li>
                        <strong>For other Business Purposes.</strong>
                        We may use your information for other Business Purposes, such as data analysis,
                        identifying usage trends, determining the effectiveness of our promotional campaigns and
                        to evaluate and improve our Services, products, marketing and your experience. We may
                        use and store this information in aggregated and anonymized form so that it is not
                        associated with individual end users and does not include personal information. We will
                        not use identifiable personal information without your consent.
                    </li>
                </ul>
            </li>

            <li id="3-will-your-information-be-shared-with-anyone">
                <h3>Will your information be shared with anyone?</h3>
                <p>
                    <strong>In Short:</strong>
                    <i>
                        We only share information with your consent, to comply with laws, to provide you with
                        services, to protect your rights, or to fulfill business obligations.
                    </i>
                </p>
                <p>We may process or share data based on the following legal basis:</p>
                <ul class="mb-4">
                    <li>
                        <strong>Consent.</strong>
                        We may process your data if you have given us specific consent to use your personal
                        information in a specific purpose.
                    </li>
                    <li>
                        <strong>Legitimate Interests.</strong>
                        We may process your data when it is reasonably necessary to achieve our legitimate
                        business interests.
                    </li>
                    <li>
                        <strong>Performance of a Contract.</strong>
                        Where we have entered into a contract with you, we may process your personal information
                        to fulfill the terms of our contract.
                    </li>
                    <li>
                        <strong>Legal Obligations.</strong>
                        We may disclose your information where we are legally required to do so in order to
                        comply with applicable law, governmental requests, a judicial proceeding, court order,
                        or legal process, such as in response to a court order or a subpoena (including in
                        response to public authorities to meet national security or law enforcement
                        requirements).
                    </li>
                    <li>
                        <strong>Vital Interests.</strong>
                        We may disclose your information where we believe it is necessary to investigate,
                        prevent, or take action regarding potential violations of our policies, suspected fraud,
                        situations involving potential threats to the safety of any person and illegal
                        activities, or as evidence in litigation in which we are involved.
                    </li>
                </ul>
                <p>
                    More specifically, we may need to process your data or share your personal information in
                    the following situations:
                </p>
                <ul class="mb-4">
                    <li>
                        <strong>Vendors, Consultants and Other Third-Party Service Providers.</strong>
                        We may share your data with third party vendors, service providers, contractors or
                        agents who perform services for us or on our behalf and require access to such
                        information to do that work. Examples include: payment processing, data analysis, email
                        delivery, hosting services, customer service and marketing efforts. We may allow
                        selected third parties to use tracking technology on the Services, which will enable
                        them to collect data about how you interact with the Services over time. This
                        information may be used to, among other things, analyze and track data, determine the
                        popularity of certain content and better understand online activity. Unless described in
                        this Policy, we do not share, sell, rent or trade any of your information with third
                        parties for their promotional purposes. We have contracts in place with our data
                        processors. This means that they cannot do anything with your personal information
                        unless we have instructed them to do it. They will not share your personal information
                        with any organization apart from us. They will hold it securely and retain it for the
                        period we instruct.
                    </li>
                    <li>
                        <strong>Business Transfers.</strong>
                        We may share or transfer your information in connection with, or during negotiations of,
                        any merger, sale of company assets, financing, or acquisition of all or a portion of our
                        business to another company.
                    </li>
                </ul>
            </li>

            <li id="4-who-will-your-information-be-shared-with">
                <h3>Who will your information be shared with?</h3>
                <p>
                    <strong>In Short:</strong>
                    We only share information with the following third parties.
                </p>
                <p>
                    We only share and disclose your information with the following third parties. We have
                    categorized each party so that you may be easily understand the purpose of our data
                    collection and processing practices. If we have processed your data based on your consent
                    and you wish to revoke your consent, please contact us.
                </p>
                <ol class="mb-4">
                    <li>
                        <strong>Advertising, Direct Marketing, and Lead Generation:</strong>
                        Google AdSense
                    </li>
                    <li>
                        <strong>Invoice and Billing:</strong>
                        Stripe
                    </li>
                    <li>
                        <strong>Retargeting Platforms:</strong>
                        Google Ads Remarketing
                    </li>
                </ol>
            </li>

            <li id="5-how-long-do-we-keep-your-information">
                <h3>How long do we keep your information?</h3>
                <p>
                    <strong>In Short:</strong>
                    We keep your information for as long as necessary to fulfill the purposes outlined in this
                    privacy policy unless otherwise required by law.
                </p>
                <p>
                    We will only keep your personal information for as long as it is necessary for the purposes
                    set out in this privacy policy, unless a longer retention period is required or permitted by
                    law (such as tax, accounting or other legal requirements). No purpose in this policy will
                    require us keeping your personal information for longer than the period of time in which
                    users have an account with us.
                </p>
                <p>
                    When we have no ongoing legitimate business need to process your personal information, we
                    will either delete or anonymize it, or, if this is not possible (for example, because your
                    personal information has been stored in backup archives), then we will securely store your
                    personal information and isolate it from any further processing until deletion is possible.
                </p>
            </li>

            <li id="6-how-do-we-keep-your-information-safe">
                <h3>How do we keep your information safe?</h3>
                <p>
                    <strong>In Short:</strong>
                    <i>
                        We aim to protect your personal information through a system of organizational and
                        technical security measures.
                    </i>
                </p>
                <p>
                    We have implemented appropriate technical and organizational security measures designed to
                    protect the security of any personal information we process. However, please also remember
                    that we cannot guarantee that the internet itself is 100% secure. Although we will do our
                    best to protect your personal information, transmission of personal information to and from
                    our Services is at your own risk. You should only access the services within a secure
                    environment.
                </p>
            </li>

            <li id="7-do-we-collect-information-from-minors">
                <h3>Do we collect information from minors?</h3>
                <p>
                    <strong>In Short:</strong>
                    We do not knowingly collect data from or market to children under 18 years of age.
                </p>
                <p>
                    We do not knowingly solicit data from or market to children under 18 years of age. By using
                    the Services, you represent that you are at least 18 or that you are the parent or guardian
                    of such a minor and consent to such minor dependent’s use of the Services. If we learn that
                    personal information from users less than 18 years of age has been collected, we will
                    deactivate the account and take reasonable measures to promptly delete such data from our
                    records. If you become aware of any data we have collected from children under age 18,
                    please contact us at
                    <a href="mailto:info@stringking.com">info@stringking.com</a>
                    .
                </p>
            </li>

            <li id="8-what-are-your-privacy-rights">
                <h3>What are your privacy rights?</h3>
                <p>
                    <strong>In Short:</strong>
                    You may review, change, or terminate your account at any time.
                </p>
                <p>
                    If you are resident in the European Economic Area and you believe we are unlawfully
                    processing your personal information, you also have the right to complain to your local data
                    protection supervisory authority. You can find their contact details here.
                </p>
                <h4>Account Information</h4>
                <p>
                    If you would at any time like to review or change the information in your account or
                    terminate your account, you can:
                </p>

                <ul class="mb-4">
                    <li>Contact us using the contact information provided.</li>
                </ul>
                <p>
                    Upon your request to terminate your account, we will deactivate or delete your account and
                    information from our active databases. However, some information may be retained in our
                    files to prevent fraud, troubleshoot problems, assist with any investigations, enforce our
                    Terms of Use and/or comply with legal requirements.
                </p>

                <p>
                    <strong>Opting out of email marketing.</strong>
                    You can unsubscribe from our marketing email list at any time by clicking on the unsubscribe
                    link in the emails that we send or by contacting us using the details provided below. You
                    will then be removed from the marketing email list – however, we will still need to send you
                    service-related emails that are necessary for the administration and use of your account. To
                    otherwise opt-out, you may:
                </p>

                <ul class="mb-4">
                    <li>Access your account settings and update preferences.</li>
                </ul>
            </li>

            <li id="9-data-breach">
                <h3>Data breach</h3>
                <p>
                    A privacy breach occurs when there is unauthorized access to or collection, use, disclosure
                    or disposal of personal information. You will be notified about data breaches when String
                    King Lacrosse LLC believes you are likely to be at risk of serious harm. For example, a data
                    breach may be likely to result in serious financial harm or harm to your mental or physical
                    well-being. In the event that String King Lacrosse LLC becomes aware of a security breach
                    which has resulted or may result in unauthorized access, use or disclosure of personal
                    information String King Lacrosse LLC will promptly investigate the matter and notify the
                    applicable Supervisory Authority not later than 72 hours after having become aware of it,
                    unless the personal data breach is unlikely to result in a risk to the rights and freedoms
                    of natural persons.
                </p>
            </li>

            <li id="10-controls-for-do-not-track-features">
                <h3>Controls for do-Not-Track features</h3>
                <p>
                    Most web browsers and some mobile operating systems and mobile applications include a
                    Do-Not-Track (&#8220;DNT&#8221;) feature or setting you can activate to signal your privacy
                    preference not to have data about your online browsing activities monitored and collected.
                    No uniform technology standard for recognizing and implementing DNT signals has been
                    finalized. As such, we do not currently respond to DNT browser signals or any other
                    mechanism that automatically communicates your choice not to be tracked online. If a
                    standard for online tracking is adopted that we must follow in the future, we will inform
                    you about that practice in a revised version of this privacy policy.
                </p>
            </li>

            <li id="11-do-california-residents-have-specific-privacy-rights">
                <h3>Do California residents have specific privacy rights?</h3>
                <p>
                    <strong>In Short:</strong>
                    Yes, if you are a resident of California, you are granted specific rights regarding access
                    to your personal information.
                </p>
                <p>
                    California Civil Code Section 1798.83, also known as the “Shine The Light” law, permits our
                    users who are California residents to request and obtain from us, once a year and free of
                    charge, information about categories of personal information (if any) we disclosed to third
                    parties for direct marketing purposes and the names and addresses of all third parties with
                    which we shared personal information in the immediately preceding calendar year. If you are
                    a California resident and would like to make such a request, please submit your request in
                    writing to us using the contact information provided below.
                </p>
                <p>
                    If you are under 18 years of age, reside in California, and have a registered account with
                    the Services, you have the right to request removal of unwanted data that you publicly post
                    on the Services. To request removal of such data, please contact us using the contact
                    information provided below, and include the email address associated with your account and a
                    statement that you reside in California. We will make sure the data is not publicly
                    displayed on the Services, but please be aware that the data may not be completely or
                    comprehensively removed from our systems.
                </p>
            </li>

            <li id="12-do-we-make-updates-to-this-policy">
                <h3>Do we make updates to this policy?</h3>
                <p>
                    <strong>In Short:</strong>
                    Yes, we will update this policy as necessary to stay compliant with relevant laws.
                </p>
                <p>
                    We may update this privacy policy from time to time. The updated version will be indicated
                    by an updated “Revised” date and the updated version will be effective as soon as it is
                    accessible. If we make material changes to this privacy policy, we may notify you either by
                    prominently posting a notice of such changes or by directly sending you a notification. We
                    encourage you to review this privacy policy frequently to be informed of how we are
                    protecting your information.
                </p>
            </li>

            <li id="13-how-can-you-contact-us-about-this-policy">
                <h3>How can you contact us about this policy?</h3>
                <p>
                    If you have questions or comments about this policy, you may email us at
                    <a href="mailto:info@stringking.com">info@stringking.com</a>
                    or by mail at:
                </p>

                <address>
                    String King Lacrosse LLC
                    <br />
                    19100 S Vermont Ave
                    <br />
                    Gardena, CA 90248
                    <br />
                    United States
                </address>
            </li>
        </ol>
    </ContainerContent>
</template>

<script lang="ts" setup>
    import ContainerContent from '~/components/ContainerContent/ContainerContent.vue';

    definePageMeta({
        scrollToTop: true
    });

    const $config = useRuntimeConfig();
    const brandTitle = $config.public.brand.title;

    useHead({
        title: `Privacy Policy | ${brandTitle}`,
        meta: [
            {
                name: 'description',
                content: 'Privacy Policy'
            }
        ]
    });
</script>
